import {
  Component,
  Input,
  forwardRef,
  HostListener,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  OnDestroy,
  HostBinding,
  NgZone,
  Inject,
  AfterViewInit,
  OnInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator } from '@angular/forms';
import { Value, AbstractValueAccessor } from './models/abstract-value-accessor.model';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { DfMonthpickerKeyboardService } from './services/keyboard.service';
import { DfMonthpickerService } from './services/monthpicker.service';
import { IState, Modes } from './models/component-state.model';
import { Subscription, fromEvent, asyncScheduler } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { filter, subscribeOn } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'df-monthpicker',
  templateUrl: './monthpicker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true
    },
    DfMonthpickerService
  ]
})
export class MonthPickerComponent extends AbstractValueAccessor
  implements ControlValueAccessor, Validator, OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription;
  state = <IState>{};
  baseYear: number;

  @Input() set monthNames(value: string[]) {
    this.componentService.update({ monthNames: value });
  }
  @Input() set disabled(value: boolean) {
    this.componentService.update({ disabled: value });
  }
  @Input() set mask(value: string) {
    this.componentService.update({ mask: value });
  }
  @Input() set title(value: string) {
    this.componentService.update({ title: value });
  }
  @Input() set resetButton(value: string) {
    this.componentService.update({ resetButton: value });
  }
  @Input() set doneButton(value: string) {
    this.componentService.update({ doneButton: value });
  }
  @Input() declare errorMessage: string;
  @Input() valid = true;
  get isValid() {
    return this.valid && this.internalValid;
  }

  @ViewChild('input', { static: false })
  private input: ElementRef;
  @ViewChild(NgbDropdown, { static: false })
  private dropdown: NgbDropdown;
  @ViewChildren('focusTrap', { read: ElementRef })
  private focusTrapList: QueryList<ElementRef>;

  @HostBinding() class = 'monthpicker';
  @HostListener('keydown', ['$event'])
  onKey(event: KeyboardEvent) {
    if (!this.dropdown.isOpen() || this.keyboardService.isEventSupported(event)) {
      return;
    }
    this.keyboardService.applyKeyboardAction(event, this.componentService, this.focusTrapList.toArray());
  }
  @HostListener('window:resize')
  updateMode() {
    const mode = this.componentService.isMobileScreen() ? Modes.mobile : Modes.month;
    if (this.state.mode !== mode) {
      this.componentService.update({ mode: mode });
    }
  }

  constructor(
    private componentService: DfMonthpickerService,
    private keyboardService: DfMonthpickerKeyboardService,
    private hostRef: ElementRef,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: any
  ) {
    super();
    this.subscriptions = new Subscription();
  }

  ngOnInit(): void {
    this.updateMode();
    this.initState();
  }

  ngAfterViewInit(): void {
    this.registerClickOutside();
  }

  private initState() {
    this.subscriptions.add(
      this.componentService
        .getState()
        .pipe(subscribeOn(asyncScheduler))
        .subscribe(this.stateChangeHandler.bind(this))
    );
  }

  private stateChangeHandler(state: IState) {
    if (this.state.value) {
      const valueCompared = this.componentService.compareValues(state.value, this.state.value);
      // close dropdown
      if (valueCompared !== 0 || (this.state.opened && !state.opened)) {
        this.dropdown.close();
      }
      // update control value
      if (valueCompared !== 0) {
        this.onChange(state.value);
      }
    }
    // update input value
    let valueString = this.componentService.formatValue(state.value);
    const inputValue = this.input.nativeElement.value;
    if (inputValue !== valueString) {
      if (state.value && state.value.month && state.value.month < 10) {
        valueString = this.componentService.padMonthWithZero(valueString);
      }
      this.input.nativeElement.value = valueString;
    }
    // update view state
    this.baseYear = this.componentService.getBaseYear();
    this.state = state;
  }

  private registerClickOutside() {
    this.ngZone.runOutsideAngular(() => {
      this.subscriptions.add(
        fromEvent<MouseEvent>(this.document, 'click')
          .pipe(filter(event => this.state.opened && !this.hostRef.nativeElement.contains(event.target)))
          .subscribe(() => this.ngZone.run(() => this.componentService.update({ opened: false })))
      );
    });
  }

  writeValue(value: Value): void {
    const update: Partial<IState> = { value };
    const isValueSelectable = value && value.month && value.year;
    if (isValueSelectable) {
      update.selected = { ...value };
    }
    this.componentService.update(update);
  }

  setDisabledState(isDisabled: boolean): void {
    this.componentService.update({ disabled: isDisabled });
  }

  inputChange() {
    const value = this.componentService.formatValueString(this.input.nativeElement.value);
    this.writeValue(value);
  }

  onClick(index: number) {
    const updateData: Partial<IState> = {
      selected: this.componentService.getValueAtIndex(index)
    };
    if (this.state.mode === Modes.month) {
      updateData.value = updateData.selected;
      updateData.opened = false;
      this.input.nativeElement.focus();
    } else {
      updateData.mode = Modes.month;
    }
    this.componentService.update({ ...updateData });
  }

  toggleMode($event: MouseEvent) {
    $event.stopPropagation();
    const mode = this.state.mode === Modes.year ? Modes.month : Modes.year;
    this.componentService.update({ mode: mode });
  }

  addYear($event: MouseEvent, increment: number) {
    $event.stopPropagation();
    const delta = this.state.mode === Modes.year ? 10 * increment : increment;
    this.componentService.update({
      selected: {
        ...this.state.selected,
        year: this.state.selected.year + delta
      }
    });
  }

  dropdownToggled(isOpen: boolean) {
    this.componentService.update({ opened: isOpen });
  }

  getItemClasses = (index: number) =>
    this.state.mode === Modes.month
      ? '' +
        (this.componentService.isSelectedMonth(index) ? ' selected' : '') +
        (this.componentService.isTodayMonth(index) ? ' today' : '')
      : '' +
        (this.componentService.isSelectedYear(index) ? ' selected' : '') +
        (this.componentService.isTodayYear(index) ? ' today' : '') +
        (index === 0 || index === 11 ? ' outrange' : '');

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
